<template>
  <v-card flat class="futuristic-card">
    <v-tabs v-model="mainTab" slider-color="primary">
      <v-tab key="allMembers" ripple class="earth-color">MEMBERS</v-tab>
      <v-tab key="corporateMembers" ripple class="earth-color">CORPORATE MEMBERS</v-tab>

      <v-tab-item key="allMembers">
        <v-card flat>
          <!-- Membership Type Tabs -->
          <v-tabs v-model="membershipTypeTab" slider-color="secondary" class="type-tabs" v-if="membershipTypes.length > 0">
            <v-tab v-for="(type, index) in membershipTypes" :key="'type-' + index" ripple>
              {{ type.label }} ({{ getFilteredMembers(type.value).length }})
            </v-tab>

            <v-tab-item v-for="(type, index) in membershipTypes" :key="'type-content-' + index">
              <v-card-title class="card-header">
                <h4 class="earth-color">{{ type.label.toUpperCase() }} ({{ getFilteredMembers(type.value).length }})</h4>
                <v-spacer></v-spacer>
                <v-text-field append-icon="mdi-magnify"
                              :label="'Search ' + type.label + ' Members'"
                              single-line
                              hide-details
                              v-model="typeSearch[type.value]"
                              class="futuristic-input"></v-text-field>
              </v-card-title>
              <v-data-table :headers="getColumnsForType(type.value)"
                            :items="getFilteredMembers(type.value)"
                            :search="typeSearch[type.value]"
                            :items-per-page-options="[10, 25, { text: 'All', value: -1 }]"
                            :disable-initial-sort="true"
                            :loading="loading"
                            class="futuristic-table">
                <v-progress-linear slot="progress" color="primary" indeterminate></v-progress-linear>
                <template v-slot:items="props">
                  <td>{{ props.item.memberID }}</td>
                  <td>{{ props.item.full_name }}</td>
                  <td>{{ props.item.gender ? props.item.gender[0] : '' }}</td>
                  <td>{{ props.item.household_type }}</td>
                  <td>{{ props.item.level }} ({{ props.item.residence }} - {{ props.item.setting }})</td>
                  <td>{{ props.item.family_count }}</td>
                  <td>KES {{ $roundOff(props.item.membership_fee) }}</td>
                  <td>KES {{ $roundOff(props.item.amount_paid || 0) }}</td>
                  <td v-if="type.value === 'driver/tour operator' || type.value === 'driver' || type.value === 'tour operator' || type.value.includes('driver') || type.value.includes('tour') || type.value.includes('operator')">
                    {{ props.item.number_plate || 'N/A' }}
                  </td>
                  <td v-if="type.value === 'driver/tour operator' || type.value === 'driver' || type.value === 'tour operator' || type.value.includes('driver') || type.value.includes('tour') || type.value.includes('operator')">
                    {{ props.item.tour_company || 'N/A' }}
                  </td>
                  <td :style="{ backgroundColor: props.item.payment_status ? '#4cd137' : '#e84118' }">
                    <span>{{ props.item.payment_status ? 'Paid' : 'Pending Payment' }}</span>
                  </td>
                  <td>
                    <v-btn depressed
                           color="primary"
                           small
                           @click="viewMember(props.item.public_id)"
                           class="futuristic-btn">
                      View Details
                    </v-btn>
                  </td>
                </template>
                <template v-slot:footer>
                  <td :colspan="getColumnsForType(type.value).length" class="footer-cell">
                    <vue-json-to-csv :json-data="getFilteredMembers(type.value)"
                                     :csv-title="type.label + ' Members List'"
                                     :labels="getCsvLabelsForType(type.value)">
                      <v-btn depressed color="primary" class="futuristic-btn">
                        <b>Download CSV</b>
                      </v-btn>
                    </vue-json-to-csv>
                  </td>
                </template>
                <v-alert slot="no-results"
                         :value="true"
                         color="error"
                         icon="mdi-alert"
                         class="futuristic-alert">
                  Your search for "{{ typeSearch[type.value] }}" found no results.
                </v-alert>
                <v-alert slot="no-data"
                         :value="true"
                         color="info"
                         icon="mdi-information"
                         class="futuristic-alert">
                  No {{ type.label.toLowerCase() }} members found.
                </v-alert>
              </v-data-table>
            </v-tab-item>
          </v-tabs>

          <!-- Fallback when no membership types are detected -->
          <div v-else>
            <v-card-title class="card-header">
              <h4 class="earth-color">ALL MEMBERS ({{ allMembers.length }})</h4>
              <v-spacer></v-spacer>
              <v-text-field append-icon="mdi-magnify"
                            label="Search All Members"
                            single-line
                            hide-details
                            v-model="search"
                            class="futuristic-input"></v-text-field>
            </v-card-title>
            <v-data-table :headers="getColumnsForType('all')"
                          :items="allMembers"
                          :search="search"
                          :items-per-page-options="[10, 25, { text: 'All', value: -1 }]"
                          :disable-initial-sort="true"
                          :loading="loading"
                          class="futuristic-table">
              <v-progress-linear slot="progress" color="primary" indeterminate></v-progress-linear>
              <template v-slot:items="props">
                <td>{{ props.item.memberID }}</td>
                <td>{{ props.item.full_name }}</td>
                <td>{{ props.item.gender ? props.item.gender[0] : '' }}</td>
                <td>{{ props.item.household_type }}</td>
                <td>{{ props.item.level }} ({{ props.item.residence }} - {{ props.item.setting }})</td>
                <td>{{ props.item.family_count }}</td>
                <td>KES {{ $roundOff(props.item.membership_fee) }}</td>
                <td>KES {{ $roundOff(props.item.amount_paid || 0) }}</td>
                <td :style="{ backgroundColor: props.item.payment_status ? '#4cd137' : '#e84118' }">
                  <span>{{ props.item.payment_status ? 'Paid' : 'Pending Payment' }}</span>
                </td>
                <td>
                  <v-btn depressed
                         color="primary"
                         small
                         @click="viewMember(props.item.public_id)"
                         class="futuristic-btn">
                    View Details
                  </v-btn>
                </td>
              </template>
              <template v-slot:footer>
                <td :colspan="getColumnsForType('all').length" class="footer-cell">
                  <vue-json-to-csv :json-data="allMembers"
                                   csv-title="All Members List"
                                   :labels="getCsvLabelsForType('all')">
                    <v-btn depressed color="primary" class="futuristic-btn">
                      <b>Download CSV</b>
                    </v-btn>
                  </vue-json-to-csv>
                </td>
              </template>
              <v-alert slot="no-results"
                       :value="true"
                       color="error"
                       icon="mdi-alert"
                       class="futuristic-alert">
                Your search for "{{ search }}" found no results.
              </v-alert>
              <v-alert slot="no-data"
                       :value="true"
                       color="info"
                       icon="mdi-information"
                       class="futuristic-alert">
                No members found.
              </v-alert>
            </v-data-table>
          </div>
        </v-card>
      </v-tab-item>

      <v-tab-item key="corporateMembers">
        <v-card-title class="card-header">
          <h4 class="earth-color">CORPORATE MEMBERS ({{ corporateMembers.length }})</h4>
          <v-spacer></v-spacer>
          <v-text-field append-icon="mdi-magnify"
                        label="Search Corporate Members"
                        single-line
                        hide-details
                        v-model="searchCorporate"
                        class="futuristic-input"></v-text-field>
        </v-card-title>
        <v-data-table :headers="c_columns"
                      :items="corporateMembers"
                      :search="searchCorporate"
                      :items-per-page-options="[10, 25, { text: 'All', value: -1 }]"
                      :disable-initial-sort="true"
                      :loading="loadingCorporate"
                      class="futuristic-table">
          <v-progress-linear slot="progress" color="primary" indeterminate></v-progress-linear>
          <template v-slot:items="props">
            <td>{{ props.item.memberID }}</td>
            <td>{{ props.item.company_name }}</td>
            <td>{{ props.item.town }}, {{ props.item.country }}</td>
            <td>{{ props.item.email }}</td>
            <td>{{ props.item.phone_number }}</td>
            <td>{{ props.item.contact_person_name }}</td>
            <td>{{ props.item.contact_person_phone_number }}</td>
            <td>KES {{ $roundOff(props.item.amount) }}</td>
            <td>{{ props.item.payment_method }}</td>
            <td>{{ formated_date(props.item.expiry_date) }}</td>
            <td>{{ props.item.visits }}</td>
            <td>{{ props.item.passengers }}</td>
            <td>{{ props.item.vehicles }}</td>
            <td>KES {{ $roundOff(props.item.amount_paid || 0) }}</td>
            <td :style="{ backgroundColor: props.item.payment_status === 1 ? '#4cd137' : '#e84118' }">
              <span>{{ props.item.payment_status === 1 ? 'Paid' : 'Pending Payment' }}</span>
            </td>
            <td>
              <v-btn depressed
                     color="primary"
                     small
                     @click="viewCorporateMember(props.item.memberID)"
                     class="futuristic-btn">
                View Details
              </v-btn>
            </td>
          </template>
          <template v-slot:footer>
            <td :colspan="c_columns.length" class="footer-cell">
              <vue-json-to-csv :json-data="corporateMembers"
                               csv-title="Corporate Members List"
                               :labels="{
                  memberID: { title: 'Member ID' },
                  company_name: { title: 'Name' },
                  country: { title: 'Country' },
                  town: { title: 'Town' },
                  email: { title: 'Email' },
                  contact_person_name: { title: 'Contact Person' },
                  amount: { title: 'Amount' },
                  payment_method: { title: 'Payment Method' },
                  expiry_date: { title: 'Expiry Date' },
                  visits: { title: 'Visits Allowed Per Year' },
                  passengers: { title: 'Passengers' },
                  vehicles: { title: 'Vehicles' },
                  amount_paid: { title: 'Amount Paid' },
                  payment_status: { title: 'Payment Status' },
                  payment_date: { title: 'Payment Date' }
                }">
                <v-btn depressed color="primary" class="futuristic-btn">
                  <b>Download CSV</b>
                </v-btn>
              </vue-json-to-csv>
            </td>
          </template>
          <v-alert slot="no-results"
                   :value="true"
                   color="error"
                   icon="mdi-alert"
                   class="futuristic-alert">
            Your search for "{{ searchCorporate }}" found no results.
          </v-alert>
          <v-alert slot="no-data"
                   :value="true"
                   color="info"
                   icon="mdi-information"
                   class="futuristic-alert">
            No corporate members found.
          </v-alert>
        </v-data-table>
      </v-tab-item>
    </v-tabs>
  </v-card>
</template>

<script>
  import VueJsonToCsv from "vue-json-to-csv";

  export default {
    components: { VueJsonToCsv },
    data() {
      return {
        mainTab: 0,
        membershipTypeTab: 0,
        search: "",
        searchCorporate: "",
        typeSearch: {},
        allMembersJSON: "",
        loading: true,
        loadingCorporate: true,
        baseColumns: [
          {
            text: "Member ID",
            value: "memberID"
          },
          {
            text: "Name",
            value: "full_name"
          },
          {
            text: "Gender",
            value: "gender[0]"
          },
          {
            text: "Household",
            value: "household_type[0]"
          },
          {
            text: "Membership Type",
            value: ""
          },
          {
            text: "No of Beneficiaries",
            value: "family_count"
          },
          {
            text: "Membership Fee",
            value: "membership_fee"
          },
          {
            text: "Amount Paid",
            value: "amount_paid"
          },
          {
            text: "Payment Status",
            value: "payment_status"
          },
          {
            text: "Action",
            value: ""
          }
        ],
        driverTourOperatorColumns: [
          {
            text: "Member ID",
            value: "memberID"
          },
          {
            text: "Name",
            value: "full_name"
          },
          {
            text: "Gender",
            value: "gender[0]"
          },
          {
            text: "Household",
            value: "household_type[0]"
          },
          {
            text: "Membership Type",
            value: ""
          },
          {
            text: "No of Beneficiaries",
            value: "family_count"
          },
          {
            text: "Membership Fee",
            value: "membership_fee"
          },
          {
            text: "Amount Paid",
            value: "amount_paid"
          },
          {
            text: "Number Plate",
            value: "number_plate"
          },
          {
            text: "Tour Company",
            value: "tour_company"
          },
          {
            text: "Payment Status",
            value: "payment_status"
          },
          {
            text: "Action",
            value: ""
          }
        ],
        c_columns: [
          {
            text: "Membership ID",
            value: "memberID"
          },
          {
            text: "Company Name",
            value: "company_name"
          },
          {
            text: "Address",
            value: ""
          },
          {
            text: "Company Email",
            value: "email"
          },
          {
            text: "Company PhoneNumber",
            value: "phone_number"
          },
          {
            text: "Contact Person",
            value: "contact_person_name"
          },
          {
            text: "Contact Person Phone Number",
            value: "contact_person_phone_number"
          },
          {
            text: "Amount",
            value: "amount"
          },
          {
            text: "Payment Method",
            value: "payment_method"
          },
          {
            text: "Expiry Date",
            value: "expiry_date"
          },
          {
            text: "Visits",
            value: "visits"
          },
          {
            text: "Passengers",
            value: "passengers"
          },
          {
            text: "Vehicles",
            value: "vehicles"
          },
          {
            text: "Amount Paid",
            value: "amount_paid"
          },
          {
            text: "Payment Status",
            value: "payment_status"
          },
          {
            text: "Action",
            value: ""
          }
        ]
      };
    },
    created() {
      this.fetchData();
    },
    mounted() {
      if (this.allMembers.length > 0) {
        this.loading = false;
      }
      if (this.corporateMembers.length > 0) {
        this.loadingCorporate = false;
      }
    },
    computed: {
      membershipTypes() {
        const types = new Set();

        this.allMembers.forEach(member => {
          if (member.level) {
            const level = String(member.level).trim();
            if (level) types.add(level);
          }

          if (member.residence) {
            const residence = String(member.residence).trim();
            if (residence) types.add(residence);
          }

          if (member.membership_type) {
            const membershipType = String(member.membership_type).trim();
            if (membershipType) types.add(membershipType);
          }
        });

        const typeArray = Array.from(types).filter(type => type && type !== '');
        typeArray.sort();

        const result = [{ label: 'All Members', value: 'all' }];

        typeArray.forEach(type => {
          result.push({
            label: this.formatTypeLabel(type),
            value: type.toLowerCase()
          });
        });

        result.forEach(type => {
          if (!this.typeSearch[type.value]) {
            this.$set(this.typeSearch, type.value, '');
          }
        });

        return result;
      }
    },
    methods: {
      async fetchData() {
        try {
          await Promise.all([
            this.$store.dispatch("get_all_members"),
            this.$store.dispatch("get_all_corporate_members")
          ]);
        } catch (error) {
          console.error("Error fetching members:", error);
        } finally {
          setTimeout(() => {
            this.loading = false;
            this.loadingCorporate = false;
          }, 3000);
        }
      },
      viewMember: function (publicId) {
        this.$router.push({
          name: "Member",
          params: {
            member_id: publicId
          }
        });
      },
      viewCorporateMember: function (memberID) {
        this.$router.push({
          name: "Corporate",
          params: {
            member_id: memberID
          }
        });
      },
      formated_date(date) {
        if (!date) return 'N/A';
        try {
          return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          });
        } catch (error) {
          return 'Invalid Date';
        }
      },
      formatTypeLabel(type) {
        return String(type)
          .toLowerCase()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      },
      getFilteredMembers(type) {
        if (type === 'all') {
          return this.allMembers;
        }

        return this.allMembers.filter(member => {
          const memberLevel = member.level ? String(member.level).toLowerCase().trim() : '';
          const memberResidence = member.residence ? String(member.residence).toLowerCase().trim() : '';
          const memberType = member.membership_type ? String(member.membership_type).toLowerCase().trim() : '';
          const memberSetting = member.setting ? String(member.setting).toLowerCase().trim() : '';

          return memberLevel.includes(type) ||
            memberResidence.includes(type) ||
            memberType.includes(type) ||
            memberSetting.includes(type);
        });
      },
      getColumnsForType(type) {
        const isDriverTourOperator = type.includes('driver') || type.includes('tour') || type.includes('operator');

        if (isDriverTourOperator) {
          return this.driverTourOperatorColumns;
        }

        return this.baseColumns;
      },
      getCsvLabelsForType(type) {
        const baseLabels = {
          memberID: { title: 'Member ID' },
          full_name: { title: 'Name' },
          gender: { title: 'Gender' },
          household_type: { title: 'Household' },
          level: { title: 'Membership Level' },
          residence: { title: 'Membership Type' },
          family_count: { title: 'Beneficiary Count' },
          membership_fee: { title: 'Membership Fee' },
          amount_paid: { title: 'Amount Paid' },
          payment_status: { title: 'Payment Status' },
          salesforce_uuid: { title: 'Salesforce Opportunity ID' },
          salesforce_account_id: { title: 'Salesforce Account ID' },
          salesforce_contact_id: { title: 'Salesforce Contact ID' }
        };

        const isDriverTourOperator = type.includes('driver') || type.includes('tour') || type.includes('operator');

        if (isDriverTourOperator) {
          return {
            ...baseLabels,
            number_plate: { title: 'Number Plate' },
            tour_company: { title: 'Tour Company' }
          };
        }

        return baseLabels;
      }
    },
    watch: {
      membershipTypes: {
        handler(newTypes) {
          newTypes.forEach(type => {
            if (!this.typeSearch[type.value]) {
              this.$set(this.typeSearch, type.value, '');
            }
          });
        },
        immediate: true
      }
    }
  };
</script>

<style scoped>
  .futuristic-card {
    border-radius: 8px;
    background: #ffffff;
    transition: all 0.3s ease;
  }

    .futuristic-card:hover {
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
    }

  .card-header {
    background: linear-gradient(45deg, #e8eaf6, #f5f7fa);
    padding: 16px;
  }

  .earth-color {
    color: #587e27;
    font-weight: 600;
    margin: 0;
  }

  .futuristic-btn {
    border-radius: 8px;
    text-transform: none;
    font-weight: 500;
    transition: all 0.2s ease;
  }

    .futuristic-btn:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }

  .futuristic-table {
    border-radius: 8px;
    margin: 0 8px 8px;
  }

  .futuristic-alert {
    border-radius: 8px;
    margin: 8px;
  }

  .footer-cell {
    padding: 16px;
    text-align: center;
  }

  @media (max-width: 960px) {
    .card-header {
      flex-direction: column;
      align-items: flex-start;
      padding: 12px;
    }

    .futuristic-table {
      margin: 0 4px 4px;
    }

    .futuristic-btn {
      font-size: 0.9rem;
    }
  }

  @media (max-width: 600px) {
    .futuristic-card {
      border-radius: 6px;
    }

    .futuristic-btn {
      font-size: 0.85rem;
    }

    .futuristic-table {
      margin: 0 4px 4px;
      overflow-x: auto;
    }
  }


  .futuristic-search .v-input__slot {
    border-radius: 8px;
    background: #f5f7fa;
  }

  .futuristic-input .v-input__slot {
    border-radius: 8px;
    background: #f5f7fa;
  }

  .futuristic-table table {
    border-collapse: separate;
    border-spacing: 0;
  }

  .futuristic-table thead tr th {
    background: linear-gradient(45deg, #e8eaf6, #f5f7fa);
    color: #587e27;
    font-weight: 600;
    font-size: 13px;
  }

  .futuristic-table tbody tr {
    transition: background-color 0.2s ease;
  }

    .futuristic-table tbody tr:hover {
      background-color: #f5f7fa;
    }

  .futuristic-link {
    color: #1976d2;
    text-decoration: none;
    font-weight: 500;
  }

    .futuristic-link:hover {
      color: #0d47a1;
    }

  .v-tabs .v-tabs__bar {
    background: linear-gradient(45deg, #e8eaf6, #f5f7fa);
  }

  .v-tabs .v-tabs__item {
    text-transform: none;
    font-weight: 500;
  }

  .type-tabs .v-tabs__bar {
    background: linear-gradient(45deg, #f5f7fa, #e8eaf6);
    margin: 0 8px;
    border-radius: 8px 8px 0 0;
  }

  .type-tabs .v-tabs__item {
    font-size: 0.85rem;
    font-weight: 500;
    color: #587e27;
    text-transform: none;
  }

  .type-tabs .v-tabs__item--active {
    background: rgba(88, 126, 39, 0.1);
  }

  @media (max-width: 960px) {
    .futuristic-search {
      max-width: 100%;
      margin-top: 12px;
    }

    .futuristic-table thead tr th {
      font-size: 12px;
      padding: 0 8px;
    }

    .futuristic-table tbody tr td {
      font-size: 12px;
      padding: 0 8px;
    }

    .type-tabs .v-tabs__item {
      font-size: 0.8rem;
      padding: 0 8px;
    }
  }

  @media (max-width: 600px) {
    .futuristic-table table {
      min-width: 700px;
    }

    .futuristic-table thead {
      display: table-header-group;
    }

    .futuristic-table tbody tr {
      display: table-row;
    }

      .futuristic-table tbody tr td {
        display: table-cell;
        font-size: 0.85rem;
        padding: 6px 8px;
      }

    .futuristic-table thead tr th {
      font-size: 0.85rem;
      padding: 6px 8px;
    }

    .type-tabs .v-tabs__wrapper {
      overflow-x: auto;
    }

    .type-tabs .v-tabs__item {
      font-size: 0.75rem;
      padding: 0 6px;
      min-width: auto;
    }

    .type-tabs .v-tabs__container {
      min-width: 500px;
    }
  }
</style>
